/** Labels and formatting for the B2B quote builder screens (spec 033). */

export const COUNTRY_OPTIONS = [
  { value: 'CL', text: 'Chile' },
  { value: 'PE', text: 'Perú' },
  { value: 'MX', text: 'México' }
];

export const LINE_OPTIONS = [
  { value: 'employee_benefit', text: 'Employee Benefit' },
  { value: 'asistencias', text: 'Asistencias' }
];

export const CHANNEL_LABELS = {
  direct: 'Directo',
  broker: 'Broker',
  assistance: 'Asistencias'
};

export const MODALITY_OPTIONS = {
  employee_benefit: [
    { value: 'collective', text: 'Colectivo (todos los colaboradores)' },
    { value: 'pool', text: 'Flexible (bolsa)' }
  ],
  asistencias: [
    { value: 'capitated', text: 'Capitado' },
    { value: 'voluntary', text: 'Voluntario' }
  ]
};

export const ASSISTANCE_PRODUCT_OPTIONS = [
  { value: 'other_insurance', text: 'Seguro de otra cosa (salud, vida, SOAP, hogar)' },
  { value: 'pet_insurance', text: 'Seguro de mascotas' },
  { value: 'virtual_alliance', text: 'Alianza solo Virtual' }
];

/** Asistencias: is the partner's product a pet insurance? Drives activation and usage. */
export const PET_PRODUCT_OPTIONS = [
  { value: 'pet_insurance', text: 'Sí' },
  { value: 'other_insurance', text: 'No' }
];

/** Standard plans in display order. Pro is sold only in Asistencias (pawer-backend spec 117). */
export const PLAN_OPTIONS = [
  { value: 'virtual', text: 'Virtual' },
  { value: 'wellness', text: 'Wellness' },
  { value: 'pro', text: 'Pro' },
  { value: 'urgencias', text: 'Urgencias' }
];

export const STATUS_LABELS = {
  draft: 'Borrador',
  sent: 'Enviada',
  won: 'Ganada',
  lost: 'Perdida'
};

export const LOST_REASON_OPTIONS = [
  { value: 'price', text: 'Precio' },
  { value: 'coverage', text: 'Coberturas' },
  { value: 'competitor', text: 'Eligió a la competencia' },
  { value: 'no_budget', text: 'Sin presupuesto o no es prioridad' },
  { value: 'no_response', text: 'Sin respuesta' },
  { value: 'other', text: 'Otro' }
];

export const CHANGE_REASON_OPTIONS = [
  { value: 'price', text: 'Precio' },
  { value: 'coverage', text: 'Coberturas' },
  { value: 'quantity', text: 'Cantidad de colaboradores' },
  { value: 'plans', text: 'Planes' },
  { value: 'other', text: 'Otro' }
];

/** Label of a lost or change reason. */
export function reasonLabel(reason) {
  const option = [...LOST_REASON_OPTIONS, ...CHANGE_REASON_OPTIONS].find((o) => o.value === reason);
  return option ? option.text : reason;
}

export const EVENT_LABELS = {
  created: 'Creada',
  status_changed: 'Cambio de estado'
};

/** Backend error codes in plain Spanish. */
const ERROR_MESSAGES = {
  MIN_MEMBERSHIPS_WITH_COVERAGE: 'Los planes con coberturas se venden desde 25 membresías.',
  UNKNOWN_SERVICE: 'Uno de los servicios no está en el catálogo de ese país.',
  COMMISSION_REQUIRED: 'Este canal no tiene comisión definida: ingrésala.',
  MODALITY_NOT_ALLOWED_FOR_LINE: 'Esa modalidad no aplica para esta línea o producto.',
  PLAN_NOT_ALLOWED_FOR_LINE: 'Ese plan no se vende en esta línea.',
  CHANNEL_NOT_ALLOWED_FOR_LINE: 'Ese canal no aplica para esta línea.',
  ASSISTANCE_PRODUCT_REQUIRED: 'Elige el tipo de producto de la asistencia.',
  VIRTUAL_ALLIANCE_NO_COVERAGE: 'Una alianza solo Virtual no lleva coberturas: usa el plan Virtual.',
  PARTNER_VARIANT_NOT_FOUND: 'La variante elegida ya no existe.',
  PRICE_NOT_POSITIVE: 'Con ese descuento el precio queda en cero.',
  PRICING_PARAMETERS_NOT_LOADED: 'Falta cargar los parámetros de precios desde el Sheet.',
  PRICING_SHEET_UNREACHABLE: 'No se pudo leer el Sheet de precios. Revisa que esté compartido con el backend.',
  PRICING_SHEET_INVALID: 'El Sheet tiene celdas con errores.',
  PRICING_APPROVER_REQUIRED: 'Solo un aprobador puede hacer esto.',
  QUOTE_NOT_FOUND: 'La cotización no existe.',
  QUOTE_NOT_EDITABLE: 'La cotización ya está finalizada: crea una versión nueva para cambiarla.',
  INVALID_STATUS_TRANSITION: 'Ese cambio de estado no se puede hacer ahora.',
  COMPANY_NOT_FOUND: 'La empresa elegida no existe.',
  EXCHANGE_RATE_MISSING: 'Falta el tipo de cambio del día.',
  PARTNER_VARIANT_NAME_TAKEN: 'Ya hay una variante con ese nombre para este broker o canal.',
  BROKER_REQUIRED: 'Elige el broker.',
  CLIENT_DIRECTORY_UNAVAILABLE: 'No se pudo conectar con Pipedrive. Intenta de nuevo.',
  SEARCH_TOO_SHORT: 'Escribe al menos 2 letras.',
  CLIENT_NOT_FOUND: 'Esa empresa ya no existe en Pipedrive.',
  CLIENT_COUNTRY_ALREADY_SET: 'La empresa ya tiene país en Pipedrive.',
  CLIENT_REQUIRED: 'Elige la empresa.',
  LINES_REQUIRED: 'Elige al menos un plan.',
  LOST_REASON_REQUIRED: 'Indica por qué se perdió.',
  PROPOSAL_NOT_CONFIGURED: 'La generación de propuestas no está configurada en este ambiente.',
  PROPOSAL_GENERATION_FAILED: 'No se pudo crear la propuesta en Google Slides. La cotización sigue en borrador: intenta de nuevo.'
};

/** Value of the "+ Crear trato nuevo" option of the deal select. */
export const NEW_DEAL = 'new';

/**
 * What went wrong after "Generar Propuesta" (no proposal configured, or
 * Pipedrive), in one sentence; null when nothing did.
 *
 * @param {string[]} warnings - `meta.warnings` of POST /b2b-quotes/:id/proposal.
 */
export function proposalWarningMessage(warnings = []) {
  if (warnings.includes('PROPOSAL_NOT_CONFIGURED')) {
    return 'La cotización quedó enviada, pero sin propuesta: falta configurar Google Drive en este ambiente.';
  }
  const deal = warnings.includes('PIPEDRIVE_DEAL_FAILED');
  const note = warnings.includes('PIPEDRIVE_NOTE_FAILED');
  if (deal && note) return 'Propuesta generada, pero no se pudo crear el trato ni dejar la nota en Pipedrive.';
  if (deal) return 'Propuesta generada, pero no se pudo crear el trato en Pipedrive: la nota quedó en la empresa.';
  if (note) return 'Propuesta generada, pero no se pudo dejar la nota en Pipedrive.';
  return null;
}

/** Items of the deal select: "+ Crear trato nuevo" first, then the open deals. */
export function dealSelectItems(deals) {
  return [{ value: NEW_DEAL, text: '+ Crear trato nuevo' }, ...deals.map(dealOption)];
}

/**
 * Form fields after choosing in the deal select: an existing deal, a new one
 * (then the pipeline is asked), or none.
 */
export function dealSelectionPatch(value) {
  if (value === NEW_DEAL) return { newDeal: true, pipedriveDealId: null };
  return { newDeal: false, pipedriveDealId: value ?? null, pipedriveNewDealPipelineId: null };
}

/** Option of the deal select: "título · etapa". */
/** "Título · Embudo · Etapa", leaving out what Pipedrive did not resolve. */
export function dealOption(deal) {
  return { value: deal.id, text: [deal.title, deal.pipeline, deal.stage].filter(Boolean).join(' · ') };
}

export function errorMessage(code) {
  return ERROR_MESSAGES[code] ?? 'Ocurrió un error. Intenta de nuevo.';
}

/** Why a Sheet cell was rejected, in plain Spanish. */
const CELL_REASONS = {
  REQUIRED: 'está vacía',
  NOT_A_NUMBER: 'no es un número',
  OUT_OF_RANGE: 'está fuera de rango',
  UNKNOWN_VALUE: 'tiene un valor desconocido',
  DUPLICATE: 'está repetida',
  MISSING_SERVICE: 'falta un servicio en algún país',
  TIER_GAP: 'los tramos no son continuos',
  MISSING_PLAN: 'un plan quedó sin servicios'
};

export function cellReason(reason) {
  return CELL_REASONS[reason] ?? reason;
}

const DECIMALS = { CLP: 0, MXN: 0, PEN: 2 };

/** Money in a quote currency, rounded only for display. */
export function formatMoney(amount, currency = 'CLP') {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  const decimals = DECIMALS[currency] ?? 0;
  const formatted = Number(amount).toLocaleString('es-CL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  const symbol = currency === 'PEN' ? 'S/ ' : '$';
  return `${symbol}${formatted}`;
}

export function formatPct(value, decimals = 0) {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(decimals).replace('.', ',')}%`;
}

export function formatTier(tier) {
  if (!tier) return '';
  const from = tier.from.toLocaleString('es-CL');
  if (tier.to === null) return `${from} o más`;
  return `${from} – ${tier.to.toLocaleString('es-CL')}`;
}

/**
 * Which actions a quote allows. A draft is edited in place; once sent (or
 * closed), "Editar" opens its next version so the sent price is kept.
 * Discounts need no approval: a draft is always sent directly.
 */
export function quoteActions(status) {
  return {
    editDraft: status === 'draft',
    send: status === 'draft',
    markClosed: status === 'sent',
    editVersion: ['sent', 'won', 'lost'].includes(status)
  };
}

/**
 * Standard plans of a line: each line uses its own version of a plan when the
 * parameters have one ("<Plan> (Employee Benefit)" / "<Plan> (Asistencias)"
 * rows of Planes), plus its own plans (Pro in Asistencias).
 */
export function plansForLine(parameters, line) {
  const shared = parameters?.plans ?? [];
  const own = (line === 'employee_benefit' ? parameters?.employeeBenefitPlans : parameters?.assistancePlans) ?? [];
  const extra = own.filter((plan) => !shared.some((s) => s.code === plan.code));
  return [...shared.map((plan) => own.find((p) => p.code === plan.code) ?? plan), ...extra];
}

